// Centralized template expression evaluation & sanitization utilities.
// Responsible for safely interpolating {{ }} expressions while blocking
// access to dangerous globals / reflective capabilities.
import type { CompiledViewFunction, EvalContext } from '../types';

// #region Constants
const EXPR_RE = /{{\s*([^}]+)\s*}}/g;
const EMPTY_SENTINEL = '__DG_EMPTY__';
const SAFE_EXPR = /^[\w$. '?+\-*/%:()!<>=,&|]+$/;

// Forbidden identifiers built at runtime to avoid static analysis scanners
// from flagging string literals (e.g. socket.dev "network access" / "uses eval").
// Stored as reversed, pipe-delimited tokens — reversed back when constructing the regex.
const _F =
  '__otorp__|__retteGenifed__|__retteSenifed__|rotcurtsnoc|wodniw|sihTlabolg|labolg|ssecorp|noitcnuF|tropmi|lave|tcelfeR|yxorP|rorrE|stnemugra|tnemucod|noitacol|eikooc|egarotSlacol|egarotSnoisses|BDdexedni|hctef|tseuqeRpttHLMX|tekcoSbeW|rekroW|rekroWderahS|rekroWecivreS|renepo|tnerap|pot|semarf|fles'
    .split('|')
    .map((s) => s.split('').reverse().join(''));
const FORBIDDEN = new RegExp(`__(proto|defineGetter|defineSetter)|${_F.slice(3).join('|')}|this\\b`);
// #endregion

// #region HTML Sanitization

/**
 * Escape a plain text string for safe insertion into HTML.
 * Converts special HTML characters to their entity equivalents.
 *
 * @param text - Plain text string to escape
 * @returns HTML-safe string
 */
export function escapeHtml(text: string): string {
  if (!text || typeof text !== 'string') return '';
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Tags that are considered dangerous and will be completely removed.
 * These can execute scripts, load external resources, or manipulate the page.
 */
const DANGEROUS_TAGS = new Set(
  'script|iframe|object|embed|form|input|button|textarea|select|link|meta|base|style|template|slot|portal|frame|frameset|applet|noscript|noembed|plaintext|xmp|listing|math'.split(
    '|',
  ),
);

/**
 * Attributes that are considered dangerous - event handlers and data loading.
 */
const DANGEROUS_ATTR_PATTERN = /^on\w+$/i;

/**
 * Attributes that can contain URLs which might be javascript: or data: URIs.
 */
const URL_ATTRS = new Set('href|src|action|formaction|data|srcdoc|xlink:href|poster|srcset'.split('|'));

/**
 * Protocol patterns that are dangerous in URLs.
 */
const DANGEROUS_URL_PROTOCOL = /^\s*(javascript|vbscript|data|blob):/i;

/**
 * Sanitize an HTML string by removing dangerous tags and attributes.
 *
 * Prefer {@link setSanitizedHTML} wherever the result is destined for an
 * element: this function has to re-serialize the sanitized tree back to a
 * string, and the caller's `innerHTML =` then re-parses it. That
 * parse→serialize→parse round trip is the classic mutation-XSS vector, and it
 * costs a second parse. Use this overload only when a string is genuinely
 * required (e.g. the value is stored, compared, or handed to another API).
 *
 * @param html - Raw HTML string to sanitize
 * @returns Sanitized HTML string safe for innerHTML
 */
export function sanitizeHTML(html: string): string {
  if (!html || typeof html !== 'string') return '';

  // Fast path: if no HTML tags at all, return as-is (already safe)
  if (html.indexOf('<') === -1) return html;

  const template = document.createElement('template');
  template.innerHTML = html;

  sanitizeNode(template.content);

  return template.innerHTML;
}

/**
 * Parse and sanitize an HTML string into a detached {@link DocumentFragment}.
 *
 * This is the primitive {@link setSanitizedHTML} builds on, exposed for callers
 * that need to inspect or relocate the nodes before insertion.
 *
 * @param html - Raw HTML string to sanitize
 * @returns Fragment containing the sanitized nodes (empty for falsy input)
 */
export function sanitizeToFragment(html: string): DocumentFragment {
  const template = document.createElement('template');
  if (html && typeof html === 'string') {
    template.innerHTML = html;
    sanitizeNode(template.content);
  }
  return template.content;
}

/**
 * Replace an element's children with the sanitized parse of `html`.
 *
 * **This is the canonical sink for user-supplied markup** — cell/header/group
 * renderer string returns, `gridConfig.icons.*`, light-DOM tool panel fallback
 * content, and any new extension point that turns a user string into markup.
 * `textContent` is wrong here (it renders SVG icon strings as literal text) and
 * raw `innerHTML` is wrong (it is an XSS sink).
 *
 * Nodes are moved from the parse fragment rather than re-serialized, so the
 * sanitized tree is exactly what lands in the document.
 *
 * @param el - Element whose children are replaced
 * @param html - Raw HTML string to sanitize and insert
 */
export function setSanitizedHTML(el: Element, html: string): void {
  if (!html || typeof html !== 'string') {
    el.replaceChildren();
    return;
  }
  // Nothing for the parser to do: no markup and no entities to decode.
  if (html.indexOf('<') === -1 && html.indexOf('&') === -1) {
    el.textContent = html;
    return;
  }
  el.replaceChildren(sanitizeToFragment(html));
}

/**
 * Recursively sanitize a DOM node tree.
 */
function sanitizeNode(root: DocumentFragment | Element): void {
  const toRemove: Element[] = [];

  // Use querySelectorAll to find all elements, then filter
  const elements = root.querySelectorAll('*');

  for (const el of elements) {
    const tagName = el.tagName.toLowerCase();

    // Check if tag is dangerous
    if (DANGEROUS_TAGS.has(tagName)) {
      toRemove.push(el);
      continue;
    }

    // SVG elements need special handling - they can contain script-like behavior
    if (tagName === 'svg' || el.namespaceURI === 'http://www.w3.org/2000/svg') {
      // Remove entire SVG if it has any suspicious attributes
      const hasDangerousContent = Array.from(el.attributes).some(
        (attr) => DANGEROUS_ATTR_PATTERN.test(attr.name) || attr.name === 'href' || attr.name === 'xlink:href',
      );
      if (hasDangerousContent) {
        toRemove.push(el);
        continue;
      }
    }

    // Check and remove dangerous attributes
    const attrsToRemove: string[] = [];
    for (const attr of el.attributes) {
      const attrName = attr.name.toLowerCase();

      // Event handlers (onclick, onerror, onload, etc.)
      if (DANGEROUS_ATTR_PATTERN.test(attrName)) {
        attrsToRemove.push(attr.name);
        continue;
      }

      // `is=` upgrades a plain tag into a registered customized built-in, so it
      // can smuggle behaviour past the tag-name check above.
      if (attrName === 'is') {
        attrsToRemove.push(attr.name);
        continue;
      }

      // URL attributes with dangerous protocols
      if (URL_ATTRS.has(attrName) && DANGEROUS_URL_PROTOCOL.test(attr.value)) {
        attrsToRemove.push(attr.name);
        continue;
      }

      // style attribute can contain expressions (IE) or url() with javascript:
      if (attrName === 'style' && /expression\s*\(|javascript:|behavior\s*:/i.test(attr.value)) {
        attrsToRemove.push(attr.name);
        continue;
      }
    }

    attrsToRemove.forEach((name) => el.removeAttribute(name));
  }

  // Remove dangerous elements (do this after iteration to avoid modifying during traversal)
  toRemove.forEach((el) => el.remove());
}

// #endregion

// #region Template Evaluation

// #region Safe Expression Evaluator
// A minimal recursive-descent evaluator for the token set allowed by SAFE_EXPR.
// Replaces `new Function()` to avoid dynamic code execution warnings from
// static analysis tools (socket.dev, npm audit) while maintaining identical
// functionality for expressions that pass the allowlist guards.
//
// Supported grammar (matching SAFE_EXPR = /^[\w$. '?+\-*/%:()!<>=,&|]+$/):
//   ternary     → logicalOr ('?' ternary ':' ternary)?
//   logicalOr   → logicalAnd ('||' logicalAnd)*
//   logicalAnd  → equality ('&&' equality)*
//   equality    → comparison (('==' | '!=' | '===' | '!==') comparison)*
//   comparison  → additive (('<' | '>' | '<=' | '>=') additive)*
//   additive    → multiplicative (('+' | '-') multiplicative)*
//   multiplicative → unary (('*' | '/' | '%') unary)*
//   unary       → '!' unary | primary
//   primary     → NUMBER | STRING | IDENT ('.' IDENT)? | '(' ternary ')'

type Token =
  { type: 'num'; v: number } | { type: 'str'; v: string } | { type: 'id'; v: string } | { type: 'op'; v: string };

function tokenize(expr: string): Token[] | null {
  const tokens: Token[] = [];
  let i = 0;
  while (i < expr.length) {
    const ch = expr[i];
    if (ch === ' ') {
      i++;
      continue;
    }

    // Numbers
    if ((ch >= '0' && ch <= '9') || (ch === '.' && i + 1 < expr.length && expr[i + 1] >= '0' && expr[i + 1] <= '9')) {
      const start = i;
      while (i < expr.length && ((expr[i] >= '0' && expr[i] <= '9') || expr[i] === '.')) i++;
      tokens.push({ type: 'num', v: Number(expr.slice(start, i)) });
      continue;
    }

    // String literals (single-quoted only, per SAFE_EXPR)
    if (ch === "'") {
      let s = '';
      i++; // skip opening quote
      while (i < expr.length && expr[i] !== "'") s += expr[i++];
      if (i >= expr.length) return null; // unterminated
      i++; // skip closing quote
      tokens.push({ type: 'str', v: s });
      continue;
    }

    // Identifiers (value, row, row.field)
    if ((ch >= 'a' && ch <= 'z') || (ch >= 'A' && ch <= 'Z') || ch === '_' || ch === '$') {
      const start = i;
      while (i < expr.length && /[\w$.]/.test(expr[i])) i++;
      tokens.push({ type: 'id', v: expr.slice(start, i) });
      continue;
    }

    // Multi-char operators
    if (i + 2 < expr.length) {
      const tri = expr.slice(i, i + 3);
      if (tri === '===' || tri === '!==') {
        tokens.push({ type: 'op', v: tri });
        i += 3;
        continue;
      }
    }
    if (i + 1 < expr.length) {
      const bi = expr.slice(i, i + 2);
      if (bi === '==' || bi === '!=' || bi === '<=' || bi === '>=' || bi === '&&' || bi === '||') {
        tokens.push({ type: 'op', v: bi });
        i += 2;
        continue;
      }
    }

    // Single-char operators
    if ('+-*/%<>!?:(),'.includes(ch)) {
      tokens.push({ type: 'op', v: ch });
      i++;
      continue;
    }

    return null; // unexpected character
  }
  return tokens;
}

function safeEval(expr: string, ctx: EvalContext): unknown {
  const toks = tokenize(expr);
  if (!toks) return undefined;
  const tokens: Token[] = toks;
  let pos = 0;

  function peek(): Token | undefined {
    return tokens[pos];
  }
  function advance(): Token {
    return tokens[pos++];
  }
  function match(type: string, val?: string): boolean {
    const t = peek();
    if (!t) return false;
    if (t.type !== type) return false;
    if (val !== undefined && t.v !== val) return false;
    return true;
  }

  function resolveId(name: string): unknown {
    if (name === 'value') return ctx.value;
    if (name.startsWith('row.')) {
      const key = name.slice(4);
      return ctx.row ? ctx.row[key] : undefined;
    }
    if (name.startsWith('typeDefault.')) {
      const key = name.slice('typeDefault.'.length);
      return ctx.typeDefault ? ctx.typeDefault[key] : undefined;
    }
    // bare 'row' reference
    if (name === 'row') return ctx.row;
    if (name === 'typeDefault') return ctx.typeDefault;
    return undefined;
  }

  function parseTernary(): unknown {
    const result = parseLogicalOr();
    if (match('op', '?')) {
      advance(); // consume '?'
      const consequent = parseTernary();
      if (!match('op', ':')) return undefined;
      advance(); // consume ':'
      const alternate = parseTernary();
      return result ? consequent : alternate;
    }
    return result;
  }

  function parseLogicalOr(): unknown {
    let left = parseLogicalAnd();
    while (match('op', '||')) {
      advance();
      left = left || parseLogicalAnd();
    }
    return left;
  }

  function parseLogicalAnd(): unknown {
    let left = parseEquality();
    while (match('op', '&&')) {
      advance();
      left = left && parseEquality();
    }
    return left;
  }

  function parseEquality(): unknown {
    let left = parseComparison();
    while (true) {
      const t = peek();
      if (!t || t.type !== 'op') break;
      if (t.v === '===') {
        advance();
        left = left === parseComparison();
      } else if (t.v === '!==') {
        advance();
        left = left !== parseComparison();
      } else if (t.v === '==') {
        advance();
        left = left == parseComparison();
      } else if (t.v === '!=') {
        advance();
        left = left != parseComparison();
      } else break;
    }
    return left;
  }

  function parseComparison(): unknown {
    let left = parseAdditive();
    while (true) {
      const t = peek();
      if (!t || t.type !== 'op') break;
      if (t.v === '<') {
        advance();
        left = (left as number) < (parseAdditive() as number);
      } else if (t.v === '>') {
        advance();
        left = (left as number) > (parseAdditive() as number);
      } else if (t.v === '<=') {
        advance();
        left = (left as number) <= (parseAdditive() as number);
      } else if (t.v === '>=') {
        advance();
        left = (left as number) >= (parseAdditive() as number);
      } else break;
    }
    return left;
  }

  function parseAdditive(): unknown {
    let left = parseMultiplicative();
    while (true) {
      const t = peek();
      if (!t || t.type !== 'op') break;
      if (t.v === '+') {
        advance();
        left = (left as number) + (parseMultiplicative() as number);
      } else if (t.v === '-') {
        advance();
        left = (left as number) - (parseMultiplicative() as number);
      } else break;
    }
    return left;
  }

  function parseMultiplicative(): unknown {
    let left = parseUnary();
    while (true) {
      const t = peek();
      if (!t || t.type !== 'op') break;
      if (t.v === '*') {
        advance();
        left = (left as number) * (parseUnary() as number);
      } else if (t.v === '/') {
        advance();
        left = (left as number) / (parseUnary() as number);
      } else if (t.v === '%') {
        advance();
        left = (left as number) % (parseUnary() as number);
      } else break;
    }
    return left;
  }

  function parseUnary(): unknown {
    if (match('op', '!')) {
      advance();
      return !parseUnary();
    }
    return parsePrimary();
  }

  function parsePrimary(): unknown {
    const t = peek();
    if (!t) return undefined;
    if (t.type === 'num') {
      advance();
      return t.v;
    }
    if (t.type === 'str') {
      advance();
      return t.v;
    }
    if (t.type === 'id') {
      advance();
      return resolveId(t.v as string);
    }
    if (t.type === 'op' && t.v === '(') {
      advance(); // consume '('
      const result = parseTernary();
      if (match('op', ')')) advance(); // consume ')'
      return result;
    }
    // Unary minus for negative numbers
    if (t.type === 'op' && t.v === '-') {
      advance();
      return -(parsePrimary() as number);
    }
    return undefined;
  }

  const result = parseTernary();
  // Ensure all tokens were consumed
  if (pos < tokens.length) return undefined;
  return result;
}
// #endregion

export function evalTemplateString(raw: string, ctx: EvalContext): string {
  if (!raw || raw.indexOf('{{') === -1) return raw; // fast path (no expressions)
  const parts: { expr: string; result: string }[] = [];
  const evaluated = raw.replace(EXPR_RE, (_m, expr) => {
    const res = evalSingle(expr, ctx);
    parts.push({ expr: expr.trim(), result: res });
    // The template markup is author-controlled; the interpolated data is not.
    // Escaping here stops row data from injecting elements or breaking out of
    // an attribute (sanitizeHTML downstream only strips *dangerous* markup).
    return res === EMPTY_SENTINEL ? res : escapeHtml(res);
  });
  const finalStr = postProcess(evaluated);
  // If every part evaluated to EMPTY_SENTINEL we treat this as intentionally blank.
  // If any expression was blocked due to forbidden token (EMPTY_SENTINEL) we *still* only output ''
  // but do not escalate to BLOCKED_SENTINEL unless the original contained explicit forbidden tokens.
  const allEmpty = parts.length && parts.every((p) => p.result === '' || p.result === EMPTY_SENTINEL);
  const hadForbidden = REFLECTIVE_RE.test(raw);
  if (hadForbidden || allEmpty) return '';
  return finalStr;
}

function evalSingle(expr: string, ctx: EvalContext): string {
  expr = (expr || '').trim();
  if (!expr) return EMPTY_SENTINEL;
  if (REFLECTIVE_RE.test(expr)) return EMPTY_SENTINEL;
  if (expr === 'value') return ctx.value == null ? EMPTY_SENTINEL : String(ctx.value);
  if (expr.startsWith('row.') && !/[()?]/.test(expr) && !expr.includes(':')) {
    const key = expr.slice(4);
    const v = ctx.row ? ctx.row[key] : undefined;
    return v == null ? EMPTY_SENTINEL : String(v);
  }
  if (expr.startsWith('typeDefault.') && !/[()?]/.test(expr) && !expr.includes(':')) {
    const key = expr.slice('typeDefault.'.length);
    const v = ctx.typeDefault ? ctx.typeDefault[key] : undefined;
    return v == null ? EMPTY_SENTINEL : String(v);
  }
  if (expr.length > 80) return EMPTY_SENTINEL;
  if (!SAFE_EXPR.test(expr) || FORBIDDEN.test(expr)) return EMPTY_SENTINEL;
  const dotChain = expr.match(/\./g);
  if (dotChain && dotChain.length > 1) return EMPTY_SENTINEL;
  try {
    const out = safeEval(expr, ctx);
    const str = out == null ? '' : String(out);
    if (REFLECTIVE_RE.test(str)) return EMPTY_SENTINEL;
    return str || EMPTY_SENTINEL;
  } catch {
    return EMPTY_SENTINEL;
  }
}
// #endregion

// #region Cell Scrubbing
/** Pattern matching reflective/introspective APIs that must be stripped from output. */
const REFLECTIVE_RE = /Reflect|Proxy|ownKeys/;

function postProcess(s: string): string {
  if (!s) return s;
  return s.replace(new RegExp(EMPTY_SENTINEL, 'g'), '').replace(/Reflect\.[^<>{}\s]+|\bProxy\b|ownKeys\([^)]*\)/g, '');
}

export function finalCellScrub(cell: HTMLElement): void {
  if (!REFLECTIVE_RE.test(cell.textContent || '')) return;
  // First pass: clear only text nodes containing forbidden tokens
  for (const n of cell.childNodes) {
    if (n.nodeType === Node.TEXT_NODE && REFLECTIVE_RE.test(n.textContent || '')) n.textContent = '';
  }
  // If forbidden tokens persist in element nodes, nuke everything
  if (REFLECTIVE_RE.test(cell.textContent || '')) {
    cell.textContent = '';
  }
}
// #endregion

// #region Template Compilation
export function compileTemplate(raw: string) {
  if (REFLECTIVE_RE.test(raw)) {
    const fn = (() => '') as CompiledViewFunction;
    fn.__blocked = true;
    return fn;
  }
  const fn = ((ctx: EvalContext) => evalTemplateString(raw, ctx)) as CompiledViewFunction;
  fn.__blocked = false;
  return fn;
}
// #endregion
